import React, { createContext, useContext, useState, useCallback } from 'react';
import { api } from '@/lib/api';
import type {
  Project,
  ProjectListItem,
  CreateProjectRequest,
  UpdateProjectRequest,
  Artifact,
  Deployment,
  Conversation,
} from '@/types';

interface ProjectContextType {
  // State
  projects: ProjectListItem[];
  activeProject: Project | null;
  artifacts: Artifact[];
  deployments: Deployment[];
  projectConversations: Conversation[];
  isLoading: boolean;
  isLoadingProject: boolean;
  error: string | null;

  // Actions
  loadProjects: () => Promise<void>;
  createProject: (data: CreateProjectRequest) => Promise<Project>;
  selectProject: (id: string) => Promise<void>;
  updateProject: (id: string, data: UpdateProjectRequest) => Promise<void>;
  deleteProject: (id: string) => Promise<void>;
  loadArtifacts: (projectId: string) => Promise<void>;
  loadDeployments: (projectId: string) => Promise<void>;
  loadProjectConversations: (projectId: string) => Promise<void>;
  deployProject: (projectId: string, environment?: string) => Promise<Deployment>;
  clearActiveProject: () => void;
  clearError: () => void;
}

const ProjectContext = createContext<ProjectContextType | undefined>(undefined);

export function ProjectProvider({ children }: { children: React.ReactNode }) {
  const [projects, setProjects] = useState<ProjectListItem[]>([]);
  const [activeProject, setActiveProject] = useState<Project | null>(null);
  const [artifacts, setArtifacts] = useState<Artifact[]>([]);
  const [deployments, setDeployments] = useState<Deployment[]>([]);
  const [projectConversations, setProjectConversations] = useState<Conversation[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingProject, setIsLoadingProject] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadProjects = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await api.listProjects();
      setProjects(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load projects');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const createProject = useCallback(async (data: CreateProjectRequest): Promise<Project> => {
    setError(null);
    try {
      const project = await api.createProject(data);
      // Add to projects list
      setProjects(prev => [{
        id: project.id,
        name: project.name,
        slug: project.slug,
        description: project.description,
        status: project.status,
        recipe_id: project.recipe_id,
        recipe_name: project.recipe_name,
        conversation_count: 0,
        artifact_count: 0,
        created_at: project.created_at,
        updated_at: project.updated_at,
      }, ...prev]);
      return project;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create project';
      setError(message);
      throw err;
    }
  }, []);

  const selectProject = useCallback(async (id: string) => {
    setIsLoadingProject(true);
    setError(null);
    try {
      const project = await api.getProject(id);
      setActiveProject(project);

      // Load related data in parallel
      const [artifactsData, deploymentsData, conversationsData] = await Promise.all([
        api.listArtifacts(id),
        api.listDeployments(id),
        api.getProjectConversations(id),
      ]);

      setArtifacts(artifactsData);
      setDeployments(deploymentsData);
      setProjectConversations(conversationsData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load project');
    } finally {
      setIsLoadingProject(false);
    }
  }, []);

  const updateProject = useCallback(async (id: string, data: UpdateProjectRequest) => {
    setError(null);
    try {
      const updated = await api.updateProject(id, data);

      // Update in projects list
      setProjects(prev => prev.map(p =>
        p.id === id ? { ...p, ...data, updated_at: updated.updated_at } : p
      ));

      // Update active project if it's the same
      if (activeProject?.id === id) {
        setActiveProject(updated);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update project';
      setError(message);
      throw err;
    }
  }, [activeProject]);

  const deleteProject = useCallback(async (id: string) => {
    setError(null);
    try {
      await api.deleteProject(id);
      setProjects(prev => prev.filter(p => p.id !== id));

      if (activeProject?.id === id) {
        setActiveProject(null);
        setArtifacts([]);
        setDeployments([]);
        setProjectConversations([]);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete project';
      setError(message);
      throw err;
    }
  }, [activeProject]);

  const loadArtifacts = useCallback(async (projectId: string) => {
    try {
      const data = await api.listArtifacts(projectId);
      setArtifacts(data);
    } catch (err) {
      console.error('Failed to load artifacts:', err);
    }
  }, []);

  const loadDeployments = useCallback(async (projectId: string) => {
    try {
      const data = await api.listDeployments(projectId);
      setDeployments(data);
    } catch (err) {
      console.error('Failed to load deployments:', err);
    }
  }, []);

  const loadProjectConversations = useCallback(async (projectId: string) => {
    try {
      const data = await api.getProjectConversations(projectId);
      setProjectConversations(data);
    } catch (err) {
      console.error('Failed to load project conversations:', err);
    }
  }, []);

  const deployProject = useCallback(async (projectId: string, environment?: string): Promise<Deployment> => {
    setError(null);
    try {
      const deployment = await api.deployProject(projectId, { environment: environment as any });
      setDeployments(prev => [deployment, ...prev]);
      return deployment;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to deploy project';
      setError(message);
      throw err;
    }
  }, []);

  const clearActiveProject = useCallback(() => {
    setActiveProject(null);
    setArtifacts([]);
    setDeployments([]);
    setProjectConversations([]);
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return (
    <ProjectContext.Provider
      value={{
        projects,
        activeProject,
        artifacts,
        deployments,
        projectConversations,
        isLoading,
        isLoadingProject,
        error,
        loadProjects,
        createProject,
        selectProject,
        updateProject,
        deleteProject,
        loadArtifacts,
        loadDeployments,
        loadProjectConversations,
        deployProject,
        clearActiveProject,
        clearError,
      }}
    >
      {children}
    </ProjectContext.Provider>
  );
}

export function useProjects() {
  const context = useContext(ProjectContext);
  if (context === undefined) {
    throw new Error('useProjects must be used within a ProjectProvider');
  }
  return context;
}
